import React, { useRef, useState } from 'react';

import { AutocompleteInputChangeReason, Avatar, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import useSWRInfinite from 'swr/infinite';

import { AsyncAutocomplete, AsyncAutocompleteProps } from '~components';
import { OffsetPagedResults } from '~interfaces';
import i18n from '~lib/i18n';

import User from '../../interfaces/user';
import UsersService from '../../services/usersService';

const service = new UsersService();

interface UserAutocompleteProps extends Omit<AsyncAutocompleteProps<User>, 'options' | 'label'> {
	label?: string;
	count?: number;
	organisationId?: string;
	userGroupId?: string;
}

const UserAutocomplete = ({
	label = i18n.t('user'),
	count = 10,
	organisationId,
	userGroupId,
	...asyncAutocompleteProps
}: UserAutocompleteProps) => {
	const { t } = useTranslation();

	const [searchQuery, setSearchQuery] = useState('');
	const [open, setOpen] = useState(false);

	// Let's set the on the first page response. Accept that it may happen
	// if an organisation is added in the mean time
	// Small changes right..?
	const total = useRef<number>(undefined);
	const page = useRef(1);

	const getKey = (i: number, previousData: OffsetPagedResults<User[]>) => {
		if (!open || (previousData && !previousData.hasMore)) {
			// Not open or reached the end
			return null;
		}

		page.current = i + 1;

		return [
			service.basePath,
			{
				page: page.current,
				pageSize: count,
				organisationId: organisationId,
				userGroupId: userGroupId,
				searchQuery: searchQuery || undefined,
			},
		] as const;
	};

	const { data, isLoading, isValidating, size, setSize } = useSWRInfinite(
		getKey,
		([_, args]) => service.getUsers(args),
		{
			keepPreviousData: true,
			revalidateFirstPage: false,
			onSuccess: (res) => (total.current = res[0].total),
		}
	);

	/**
	 * Just set the size of the pages
	 */
	const handleOverflow = () => {
		if (total.current != null && (size - 1) * count <= total.current) {
			setSize(size + 1);
		}
	};

	const handleInputChange = (
		e: React.SyntheticEvent<Element, Event>,
		value: string,
		reason: AutocompleteInputChangeReason
	) => {
		setSearchQuery(value);

		asyncAutocompleteProps.onInputChange?.(e, value, reason);
	};

	return (
		<AsyncAutocomplete
			{...asyncAutocompleteProps}
			label={label}
			loading={isLoading || isValidating}
			noOptionsText={t('noResults')}
			onOpen={() => setOpen(true)}
			onClose={() => setOpen(false)}
			options={data?.flatMap((el) => el.results) ?? []}
			onInputChange={handleInputChange}
			onOverflow={handleOverflow}
			renderOption={(props, option) => {
				const { key, ...optionProps } = props;

				return (
					<Stack component='li' {...optionProps} key={option.id} direction='row' spacing={1}>
						<Avatar src={option.imageUrl} />
						<Stack>
							<Typography>{option.label}</Typography>
							<Typography color='textSecondary'>{option.email}</Typography>
						</Stack>
					</Stack>
				);
			}}
			sx={{
				minWidth: 220,
				...asyncAutocompleteProps.sx,
			}}
		/>
	);
};

export default UserAutocomplete;
